
import Meta from '~/mixins/MetaHead'
import { getPageInfo } from '~/fc/Dal/PageInfoRepo'
import ConverterTemplateFactory from '~/fc/ConverterTemplateFactory'
import InfoParser from '~/fc/InfoParser'
import UrlParamHandler from '~/fc/UrlParamHandler'
import GroupTemplate from /* webpackChunkName: "GroupTemplate" */ '~/layouts/ConverterTemplates/GroupTemplate'
import ToolTemplate from /* webpackChunkName: "ToolTemplate" */ '~/layouts/ConverterTemplates/ToolTemplate'
import IndexTemplate from /* webpackChunkName: "IndexTemplate" */ '~/layouts/ConverterTemplates/IndexTemplate'
import PreviewTemplate from /* webpackChunkName: "PreviewTemplate" */ '~/layouts/ConverterTemplates/PreviewTemplate'
import SingleTemplate from /* webpackChunkName: "SingleTemplate" */ '~/layouts/ConverterTemplates/SingleTemplate'
import ConversionGuide from /* webpackChunkName: "ConversionGuide" */ '~/layouts/partials/page/ConversionGuide'
import Skeleton from "~/components/Skeleton.vue";
import { lazyLoadComponentIfVisible } from '~/fc/lazy-load-component';
import { fetchReviews } from '~/fc/Dal/UtilityRepo'

const UnitIndexMeta = {
    title: "Unit Converter - Convert Units Online - FreeConvert.com",
    desc: "Convert length, area, weight, temperature, and energy units online for free",
    header_h1: "Unit Converter",
    subtext_h1: "Select a Converter and enter a value to convert",
};
export default {
    name: "ConverterIndex",
    components: {
        GroupTemplate,
        IndexTemplate,
        PreviewTemplate,
        SingleTemplate,
        ToolTemplate,
        ConversionGuide,
        FileInput: () => import(/* webpackChunkName: "FileInput" */ '../../../components/file-io/FileInput'),
        Alert: () => import(/* webpackChunkName: "Alert" */ '../../../components/ui/Alert'),
        TryFree: lazyLoadComponentIfVisible({
            componentLoader: () => import(/* webpackChunkName: "TryFree" */ '../../../layouts/partials/page/TryFree'),
            loadingComponent: Skeleton,
        }),
        ContentCard: lazyLoadComponentIfVisible({
            componentLoader: () => import(/* webpackChunkName: "ContentCard" */ '../../../components/content/ContentCard'),
            loadingComponent: Skeleton,
        }),
        ContentCardHTML: lazyLoadComponentIfVisible({
            componentLoader: () => import(/* webpackChunkName: "ContentCardHTML" */ '../../../components/content/ContentCardHTML'),
            loadingComponent: Skeleton,
        }),
        AdsSide: () => import(/* webpackChunkName: "AdsSide" */ '../../../layouts/partials/ads/AdsSide'),
        AdInPage: () => import(/* webpackChunkName: "AdInPage" */ '../../../layouts/partials/ads/AdInPage'),
        FlexibleGrid: () => import(/* webpackChunkName: "flexible-grid" */ '../../../layouts/grid/flexible-grid'),
        FlexibleGridCard: () => import(/* webpackChunkName: "flexible-grid-card" */ '../../../layouts/grid/flexible-grid-card'),
        AppLinksBanner: () => import(/* webpackChunkName: "AppLinksBanner" */ '../../../components/AppLinksBanner'),
        TrustPilotReviewsCollapsible: () => import(/* webpackChunkName: "Accordion" */ "~/components/ui/TrustPilotReviewsCollapsible"),
        SecurityAndCompliance: () => import(/* webpackChunkName: "SecurityAndCompliance" */ "~/components/ui/SecurityAndCompliance")
    },
    mixins: [Meta],
    middleware: "redirection",
    async asyncData(ctx) {
        try {
            const { path } = ctx.route;
            if (path[path.length - 1] === "/") {
                return ctx.redirect(301, path.slice(0, path.length - 1));
            }
            ctx.store.commit("clear");
            ctx.store.commit("items/clear");
            ctx.store.commit("advancedSetting/clearOptions");
            const id = ctx.params.id;
            const type = id.replace("-Converter", "");

            const [info, { data: reviews }] = await Promise.all([getPageInfo(id, ctx, true), fetchReviews(ctx.app)]);

            if (info === "Page not found" || !info) {
                ctx.error({ statusCode: 404, message: "Page not found" });
            }
            const active = info
                ? ConverterTemplateFactory.setTemplate(info.template).getTemplate()
                : false;
            info.active = active;
            await ctx.store.dispatch("setInfo", info);

            if (active === "GroupTemplate") {
                ctx.store.commit("updateBreadcrumbs", [
                    {
                        url: ctx.app.localePath(
                            `/${info.target.group.toLowerCase()}-converter`
                        ),
                        label:
                            info.target.group +
                            " " +
                            ctx.app.i18n.messages[ctx.app.i18n.locale].converter,
                    },
                    {
                        url: ctx.app.localePath(`/${info.target.slug}-converter`),
                        label:
                            info.target.name +
                            " " +
                            ctx.app.i18n.messages[ctx.app.i18n.locale].converter,
                    },
                ]);
            } else if (active === "IndexTemplate") {
                const type = ctx.route.params.id.replace("-converter", "");
                ctx.store.commit("updateBreadcrumbs", [
                    {
                        url: ctx.app.localePath(`/${type}-converter`),
                        label:
                            type + " " + ctx.app.i18n.messages[ctx.app.i18n.locale].converter,
                    },
                ]);
            } else {
                ctx.store.commit("updateBreadcrumbs", [
                    {
                        url: path,
                        label: info.header_h1,
                    },
                ]);
            }

            const infoParser = new InfoParser({ $store: ctx.store });
            const conversionGuideContent = infoParser.getConversionGuide(info);
            const contentsExtra = infoParser.getExtraContent();

            return {
                active,
                type,
                title: info.title,
                desc: info.meta_description,
                header_h1: info.header_h1,
                subtext_h1: info.subtext_h1,
                currentUrl: `/convert/${id}`,
                conversionGuideContent,
                contentsExtra,
                reviews
            };
        } catch (e) {
            ctx.error({ statusCode: 404, message: "Page not found", error: e });
        }
    },
    data: () => ({
        active: null,
        type: null,
        uid: 1,
        conversionGuideContent: null,
        contentsExtra: null,
        reviews: []
    }),
    computed: {
        isMobile() {
            return this.$store.state.store.isMobile;
        },
        info() {
            return this.$store.state.info;
        },
        list() {
            return this.$store.state.list;
        },
        advancedSetting() {
            return this.$store.state.advancedSetting;
        },
        fileType() {
            return this.info.type
                ? this.info.type
                : this.info.target && this.info.target.group
                    ? this.info.target.group.toLowerCase()
                    : "";
        },
        showAdsSide() {
            return !(this.enablePreview && this.hasFiles);
        },
        infoParser() {
            return new InfoParser(this);
        },
        hasFiles() {
            return !!this.$store.state.list.length;
        },
        customVariables() {
            return this.info.customVariables;
        },
        enablePreview() {
            return (
                this.info.active === "PreviewTemplate" ||
                (this.info.customVariables
                    ? this.info.customVariables.enablePreview
                    : false)
            );
        },
        showAdsInPage() {
            return true;
        },
        showGroupContent() {
            return ["GroupTemplate", "SingleTemplate", "IndexTemplate"].includes(
                this.active
            );
        },
        showToolContent() {
            return ["ToolTemplate"].includes(this.active);
        },
        targetContent() {
            return this.info.target ? this.info.target.info : null;
        },
        sourceContent() {
            return this.info.source ? this.info.source.info : null;
        },
        specificToolsItems() {
            return this.infoParser.getSpecificToolsContent()
                ? this.infoParser.getSpecificToolsContent()
                : [];
        },
        specificTools() {
            const tools = this.specificToolsItems;
            if (!tools.length) {
                return null;
            }
            return [
                {
                    type: "title",
                    value:
                        "Specific" + this.info.target
                            ? " " + this.info.target.name
                            : "" + " Converters",
                },
                {
                    type: "paragraph",
                    value:
                        "While this page can convert any file to" +
                        (this.info.target ? " " + this.info.target.name : "") +
                        ", you may wish to visit following Converter pages that only support specific conversions",
                },
                {
                    type: "list",
                    value: {
                        column: this.isMobile ? 2 : 3,
                        items: tools,
                    },
                },
            ];
        },
        otherToolsItems() {
            return this.infoParser.getOtherToolsContent()
                ? this.infoParser.getOtherToolsContent()
                : [];
        },
        otherTools() {
            const items = this.otherToolsItems;
            if (!items.length) {
                return null;
            }
            return [
                {
                    type: "title",
                    value:
                        "Convert" +
                        (this.info.target ? " " + this.info.target.name : "") +
                        " to other file types",
                },
                {
                    type: "paragraph",
                    value:
                        "FreeConvert.com can convert your" +
                        (this.info.target ? " " + this.info.target.name : "") +
                        " files to these other formats:",
                },
                {
                    type: "list",
                    value: {
                        column: this.isMobile ? 2 : 3,
                        items: tools,
                    },
                },
            ];
        },
        relatedToolsItems() {
            return this.infoParser.getRelatedToolsContent()
                ? this.infoParser.getRelatedToolsContent()
                : [];
        },
        relatedTools() {
            const tools = this.relatedToolsItems;
            if (!tools.length) {
                return null;
            }
            return [
                {
                    type: "title",
                    value:
                        "Related" +
                        (this.info.target ? " " + this.info.target.name : "") +
                        " Converters",
                },
                {
                    type: "paragraph",
                    value:
                        "FreeConvert.com can convert your" +
                        (this.info.target ? " " + this.info.target.name : "") +
                        " files to these other formats",
                },
                {
                    type: "list",
                    value: {
                        column: 3,
                        items: tools,
                    },
                },
            ];
        },
        showPageContents() {
            return !(this.enablePreview && this.hasFiles);
        },
    },
    created() {
        if (this.info.target && this.info.target.slug) {
            this.$store.commit(
                "advancedSetting/setDefaultTarget",
                this.info.target.slug
            );
        }
    },
    mounted() {
        new UrlParamHandler(this.$route).handle();
    },
    methods: {
        setData({ info, type }) {
            this.info = { ...info, ...UnitIndexMeta };
            this.type = type;
            this.uid++;
        },
    },
};
